// src/net/report.ts — client write-only board/content report API.
//
// Reports are a trust backstop for bad/generated board content. They are sent to the backend
// operations queue and are not stored locally: if the backend is unreachable, the UI simply tells
// the player the report did not send.

import type { ReportOptions, SubmitReportInput } from './types';

const DEFAULT_TIMEOUT_MS = 4_000;

function apiBase(): string {
  const raw = import.meta.env?.VITE_API_BASE_URL ?? '';
  return raw.replace(/\/+$/, '');
}

async function withTimeout(
  fn: (signal: AbortSignal) => Promise<Response>,
  opts: ReportOptions,
): Promise<Response> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const ctrl = new AbortController();
  const onAbort = (): void => ctrl.abort();
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort();
    else opts.signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fn(ctrl.signal);
  } finally {
    clearTimeout(timer);
    if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
  }
}

export async function submitBoardReport(
  input: SubmitReportInput,
  opts: ReportOptions = {},
): Promise<{ live: boolean; id?: string }> {
  try {
    const res = await withTimeout(
      (signal) =>
        fetch(`${apiBase()}/reports`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input),
          signal,
        }),
      opts,
    );
    if (!res.ok) return { live: false };
    const body = (await res.json()) as { accepted?: boolean; id?: unknown };
    if (body?.accepted !== true) return { live: false };
    return { live: true, id: typeof body.id === 'string' ? body.id : undefined };
  } catch {
    return { live: false };
  }
}
